// src/pages/MetricasBolsasFinancieras.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import {
    FaWallet, FaArrowUp, FaArrowDown,
    FaFileExcel, FaPrint, FaCogs,
    FaSearch, FaLayerGroup, FaShieldAlt, FaInfoCircle, FaTimes, FaExchangeAlt
} from 'react-icons/fa';
import {
    PLANTILLA_EGRESOS_AGAVE_V,
    normalizarCategoriasEgreso,
    obtenerCategoriasQueCompartenConcepto,
    obtenerBolsaDeIngreso,
    extraerDescCuotaExtra,
    partidaCoincideConConcepto
} from '../utils/bolsasFinancierasUtils';
import './MetricasBolsasFinancieras.css';

const COLORES_ZEN = [
    '#10b981', '#3b82f6', '#6366f1', '#f59e0b', '#ec4899', '#8b5cf6',
    '#06b6d4', '#14b8a6', '#f97316', '#64748b'
];

const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

// Tooltip Zen para Gráfica de Barras con Desglose de Fondo Común
const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="custom-chart-tooltip">
                <p className="tooltip-title"><strong>{data.nombreCompleto}</strong></p>
                {data.esCompartida && data.categoriasVinculadas?.length > 0 && (
                    <p className="tooltip-shared-hint">
                        🔗 Fondo común con: {data.categoriasVinculadas.join(', ')}
                    </p>
                )}
                {data.RecaudadoExclusivo > 0 && (
                    <div className="tooltip-row text-green">
                        <span>Recaudado Exclusivo:</span>
                        <strong>{formatCurrency(data.RecaudadoExclusivo)}</strong>
                    </div>
                )}
                {data.FondoComun > 0 && (
                    <div className="tooltip-row text-blue">
                        <span>Fondo Común Compartido:</span>
                        <strong>{formatCurrency(data.FondoComun)}</strong>
                    </div>
                )}
                <div className="tooltip-row font-semibold">
                    <span>Capacidad Total:</span>
                    <strong>{formatCurrency(data.Recaudado)}</strong>
                </div>
                <div className="tooltip-row text-red">
                    <span>Gastado Propio:</span>
                    <strong>{formatCurrency(data.Gastado)}</strong>
                </div>
                {data.GastadoVinculadas > 0 && (
                    <div className="tooltip-row text-amber-dark">
                        <span>Consumo común por vinculadas:</span>
                        <strong>-{formatCurrency(data.GastadoVinculadas)}</strong>
                    </div>
                )}
                <div className="tooltip-divider" />
                <div className={`tooltip-row ${data.Saldo >= 0 ? 'text-emerald' : 'text-rose'}`}>
                    <span>Saldo Remanente:</span>
                    <strong>{formatCurrency(data.Saldo)}</strong>
                </div>
            </div>
        );
    }
    return null;
};

const MetricasBolsasFinancieras = () => {
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    // Estados de datos crudos
    const [ingresos, setIngresos] = useState([]);
    const [egresos, setEgresos] = useState([]);
    const [categoriasEgreso, setCategoriasEgreso] = useState([]);
    const [receiptConfig, setReceiptConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    // Estados de filtros
    const [periodo, setPeriodo] = useState('mes'); // 'semana' | 'mes' | 'año' | 'historico' | 'custom'
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [bolsaSeleccionada, setBolsaSeleccionada] = useState('todas');
    const [filtroTipoGasto, setFiltroTipoGasto] = useState('todos'); // 'todos' | 'Fijo' | 'Variable'
    const [busquedaTexto, setBusquedaTexto] = useState('');
    const [tabGrafica, setTabGrafica] = useState('comparativa'); // 'comparativa' | 'distribucion' | 'tendencia'
    const [bolsaDetalleModal, setBolsaDetalleModal] = useState(null);
    const [tabModalMovimientos, setTabModalMovimientos] = useState('ingresos'); // 'ingresos' | 'egresos'
    const [paginaModalIngresos, setPaginaModalIngresos] = useState(1);
    const [paginaModalEgresos, setPaginaModalEgresos] = useState(1);
    const REGISTROS_POR_PAGINA_MODAL = 8;
    const [tabAuditoria, setTabAuditoria] = useState('egresos'); // 'egresos' | 'ingresos'

    // Helper para abrir modal de detalle con valores iniciales limpios
    const handleAbrirDetalleModal = (bolsa) => {
        setBolsaDetalleModal(bolsa);
        if (bolsa.movimientosIngreso?.length > 0) {
            setTabModalMovimientos('ingresos');
        } else if (bolsa.movimientosEgreso?.length > 0) {
            setTabModalMovimientos('egresos');
        } else {
            setTabModalMovimientos('ingresos');
        }
        setPaginaModalIngresos(1);
        setPaginaModalEgresos(1);
    };

    // Inicializar fechas para período mensual por defecto
    useEffect(() => {
        const hoy = new Date();
        const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const ultimoDiaMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);

        setFechaInicio(primerDiaMes.toISOString().split('T')[0]);
        setFechaFin(ultimoDiaMes.toISOString().split('T')[0]);
    }, []);

    // Cargar configuración de recibos y categorías
    useEffect(() => {
        if (!fraccionamientoId) return;

        const cargarConfiguracion = async () => {
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccRef);
                if (snap.exists()) {
                    const cfg = snap.data().configuracionRecibos || {};
                    setReceiptConfig(cfg);

                    const categoriasNorm = normalizarCategoriasEgreso(
                        cfg.categoriasEgreso,
                        cfg.conceptosEgresoList || cfg.conceptosEgreso
                    );

                    if (categoriasNorm.length > 0) {
                        setCategoriasEgreso(categoriasNorm);
                    } else {
                        // Fallback canónico de Agave V
                        setCategoriasEgreso(PLANTILLA_EGRESOS_AGAVE_V);
                    }
                } else {
                    setCategoriasEgreso(PLANTILLA_EGRESOS_AGAVE_V);
                }
            } catch (err) {
                console.error('Error cargando configuración de bolsas:', err);
                setCategoriasEgreso(PLANTILLA_EGRESOS_AGAVE_V);
            }
        };

        cargarConfiguracion();
    }, [fraccionamientoId]);

    // Escuchar Ingresos en tiempo real
    useEffect(() => {
        if (!fraccionamientoId) return;

        const refIngresos = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
        const qIngresos = query(refIngresos, orderBy('fecha', 'desc'));

        const unsubIngresos = onSnapshot(qIngresos, (snapshot) => {
            const list = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(d => !d.deleted && d.metodoPago !== 'registro_automatico');
            setIngresos(list);
            setLoading(false);
        }, (err) => {
            console.error('Error escuchando ingresos:', err);
            setLoading(false);
        });

        return () => unsubIngresos();
    }, [fraccionamientoId]);

    // Escuchar Egresos en tiempo real
    useEffect(() => {
        if (!fraccionamientoId) return;

        const refEgresos = collection(db, 'fraccionamientos', fraccionamientoId, 'egresos');
        const qEgresos = query(refEgresos, orderBy('fecha', 'desc'));

        const unsubEgresos = onSnapshot(qEgresos, (snapshot) => {
            const list = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(d => !d.deleted);
            setEgresos(list);
        }, (err) => {
            console.error('Error escuchando egresos:', err);
        });

        return () => unsubEgresos();
    }, [fraccionamientoId]);

    // Cambiar rango rápido de período
    const handleCambiarPeriodo = (tipoPeriodo) => {
        setPeriodo(tipoPeriodo);
        const hoy = new Date();

        if (tipoPeriodo === 'semana') {
            const primerDiaSemana = new Date(hoy);
            const diaSemana = hoy.getDay(); // 0 domingo, 1 lunes...
            const diff = hoy.getDate() - diaSemana + (diaSemana === 0 ? -6 : 1);
            primerDiaSemana.setDate(diff);
            const finSemana = new Date(primerDiaSemana);
            finSemana.setDate(primerDiaSemana.getDate() + 6);

            setFechaInicio(primerDiaSemana.toISOString().split('T')[0]);
            setFechaFin(finSemana.toISOString().split('T')[0]);
        } else if (tipoPeriodo === 'mes') {
            const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
            const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
            setFechaInicio(primerDia.toISOString().split('T')[0]);
            setFechaFin(ultimoDia.toISOString().split('T')[0]);
        } else if (tipoPeriodo === 'año') {
            const primerDiaAño = new Date(hoy.getFullYear(), 0, 1);
            const ultimoDiaAño = new Date(hoy.getFullYear(), 11, 31);
            setFechaInicio(primerDiaAño.toISOString().split('T')[0]);
            setFechaFin(ultimoDiaAño.toISOString().split('T')[0]);
        } else if (tipoPeriodo === 'historico') {
            setFechaInicio('');
            setFechaFin('');
        }
    };

    // Helper: Extraer fecha segura (objeto Date)
    const parseFechaSegura = (item) => {
        if (!item) return null;
        if (item.fecha?.toDate) return item.fecha.toDate();
        if (item.fecha instanceof Date) return item.fecha;
        if (item.timestamp?.toDate) return item.timestamp.toDate();
        if (typeof item.fecha === 'string') {
            const d = new Date(item.fecha);
            if (!isNaN(d.getTime())) return d;
        }
        return null;
    };

    // Filtrar Ingresos por rango de fecha y búsqueda
    const ingresosFiltrados = useMemo(() => {
        return ingresos.filter(ing => {
            const f = parseFechaSegura(ing);
            if (!f) return false;

            if (fechaInicio) {
                const fIni = new Date(fechaInicio + 'T00:00:00');
                if (f < fIni) return false;
            }
            if (fechaFin) {
                const fFin = new Date(fechaFin + 'T23:59:59');
                if (f > fFin) return false;
            }

            if (busquedaTexto.trim()) {
                const term = busquedaTexto.toLowerCase();
                const conceptoStr = (ing.concepto || '').toLowerCase();
                const clienteStr = (ing.nombreCliente || ing.propietario || '').toLowerCase();
                const folioStr = (ing.folio || '').toLowerCase();
                if (!conceptoStr.includes(term) && !clienteStr.includes(term) && !folioStr.includes(term)) {
                    return false;
                }
            }

            return true;
        });
    }, [ingresos, fechaInicio, fechaFin, busquedaTexto]);

    // Filtrar Egresos por rango de fecha, tipo de gasto y búsqueda
    const egresosFiltrados = useMemo(() => {
        return egresos.filter(eg => {
            const f = parseFechaSegura(eg);
            if (!f) return false;

            if (fechaInicio) {
                const fIni = new Date(fechaInicio + 'T00:00:00');
                if (f < fIni) return false;
            }
            if (fechaFin) {
                const fFin = new Date(fechaFin + 'T23:59:59');
                if (f > fFin) return false;
            }

            // Filtro Fijo / Variable
            const tipo = eg.tipoGasto || eg['Tipo de Gasto:'] || 'Variable';
            if (filtroTipoGasto !== 'todos' && tipo !== filtroTipoGasto) {
                return false;
            }

            if (busquedaTexto.trim()) {
                const term = busquedaTexto.toLowerCase();
                const descStr = (eg.descripcion || '').toLowerCase();
                const catStr = (eg.categoria || '').toLowerCase();
                const provStr = (eg.proveedor || '').toLowerCase();
                const folioStr = (eg.folio || '').toLowerCase();
                if (!descStr.includes(term) && !catStr.includes(term) && !provStr.includes(term) && !folioStr.includes(term)) {
                    return false;
                }
            }

            return true;
        });
    }, [egresos, fechaInicio, fechaFin, filtroTipoGasto, busquedaTexto]);

    // Aplanar y desglosar partidas de ingresos (resuelve tickets multi-concepto sin duplicación)
    const partidasIngresosFiltradas = useMemo(() => {
        const partidas = [];

        ingresosFiltrados.forEach(ing => {
            const fSegura = parseFechaSegura(ing);

            if (Array.isArray(ing.pagos) && ing.pagos.length > 0) {
                ing.pagos.forEach((p, idx) => {
                    const montoPartida = Number(p.montoNeto != null ? p.montoNeto : (p.monto != null ? p.monto : 0));
                    const cuotaExtraDesc = extraerDescCuotaExtra(p, ing);
                    partidas.push({
                        id: `${ing.id}_partida_${idx}`,
                        ticketId: ing.id,
                        folio: ing.folio || 'N/A',
                        fecha: ing.fecha,
                        fechaSegura: fSegura,
                        concepto: p.concepto || ing.concepto || 'Ingreso',
                        monto: montoPartida,
                        nombreCliente: ing.nombreCliente || ing.propietario || 'S/N',
                        metodoPago: p.metodoPago || ing.metodoPago || 'N/A',
                        detalle: p.detalle || ing.detalle || '',
                        observaciones: p.observaciones || ing.observaciones || '',
                        cuotaExtraDesc,
                        cuotaExtraId: p.cuotaExtraId || ing.cuotaExtraId || null,
                        esMultiConcepto: true,
                        numeroPartida: idx + 1,
                        totalPartidas: ing.pagos.length,
                        rawIngreso: ing
                    });
                });
            } else {
                const montoTotal = Number(ing.montoTotal != null ? ing.montoTotal : (ing.montoNeto != null ? ing.montoNeto : (ing.monto != null ? ing.monto : 0)));
                const cuotaExtraDesc = extraerDescCuotaExtra(ing, ing);
                partidas.push({
                    id: ing.id,
                    ticketId: ing.id,
                    folio: ing.folio || 'N/A',
                    fecha: ing.fecha,
                    fechaSegura: fSegura,
                    concepto: ing.concepto || 'Ingreso',
                    monto: montoTotal,
                    nombreCliente: ing.nombreCliente || ing.propietario || 'S/N',
                    metodoPago: ing.metodoPago || 'N/A',
                    detalle: ing.detalle || '',
                    observaciones: ing.observaciones || '',
                    cuotaExtraDesc,
                    cuotaExtraId: ing.cuotaExtraId || null,
                    esMultiConcepto: false,
                    numeroPartida: 1,
                    totalPartidas: 1,
                    rawIngreso: ing
                });
            }
        });

        return partidas;
    }, [ingresosFiltrados]);

    // Mapeo Inteligente por Bolsas Financieras (Alternativa B: Fondo Común Desglosado con Aislamiento y Congelados)
    const metricasBolsas = useMemo(() => {
        if (!categoriasEgreso || categoriasEgreso.length === 0) return [];

        // Helper para comparar conceptos de forma flexible
        const coincideConcepto = (concA, concB) => {
            const a = (concA || '').toLowerCase().trim();
            const b = (concB || '').toLowerCase().trim();
            if (!a || !b) return false;
            return a === b || a.includes(b) || b.includes(a);
        };

        const bolsasProyecto = categoriasEgreso.filter(c => Boolean(c.esBolsaProyecto));

        // Paso 1: Pre-análisis individual de cada categoría
        const analisisInicial = categoriasEgreso.map((cat, idx) => {
            const catNombreNorm = (cat.nombre || '').toLowerCase().trim();
            const conceptosIngreso = cat.conceptosIngresoAsociados || [];
            const conceptosEspecificos = (cat.conceptos || []).map(c => {
                const nom = typeof c === 'string' ? c : c.nombre;
                return (nom || '').toLowerCase().trim();
            });

            // Clasificar conceptos en Exclusivos y Compartidos
            const conceptosExclusivos = [];
            const conceptosCompartidos = [];
            const hermanasSet = new Set();

            if (cat.esBolsaProyecto) {
                // Las bolsas de proyecto tienen fondeo exclusivo dedicado
                conceptosExclusivos.push(cat.nombre);
                if (cat.cuotaExtraAsociadaId) conceptosExclusivos.push(cat.cuotaExtraAsociadaId);
            } else {
                conceptosIngreso.forEach(ci => {
                    const hermanas = obtenerCategoriasQueCompartenConcepto(ci, categoriasEgreso, cat.id);
                    if (hermanas.length > 0) {
                        conceptosCompartidos.push(ci);
                        hermanas.forEach(h => hermanasSet.add(h));
                    } else {
                        conceptosExclusivos.push(ci);
                    }
                });
            }

            const categoriasVinculadas = Array.from(hermanasSet);
            const esBolsaMadreCompartida = !cat.esBolsaProyecto && conceptosCompartidos.length > 0;

            // Filtrar partidas de ingreso exclusivas y compartidas con aislamiento estricto de proyectos
            const partidasExclusivas = [];
            const partidasCompartidas = [];

            partidasIngresosFiltradas.forEach(partida => {
                const partConceptoNorm = (partida.concepto || '').toLowerCase().trim();
                const partDescNorm = (partida.cuotaExtraDesc || '').toLowerCase().trim();
                const partId = String(partida.cuotaExtraId || '');

                if (cat.esBolsaProyecto) {
                    const coincideProyecto =
                        (cat.cuotaExtraAsociadaId && partId === String(cat.cuotaExtraAsociadaId)) ||
                        (partDescNorm && (partDescNorm === catNombreNorm || catNombreNorm.includes(partDescNorm) || partDescNorm.includes(catNombreNorm))) ||
                        partConceptoNorm.includes(catNombreNorm) ||
                        (partida.detalle && String(partida.detalle).toLowerCase().includes(catNombreNorm)) ||
                        (partida.observaciones && String(partida.observaciones).toLowerCase().includes(catNombreNorm));

                    if (coincideProyecto) {
                        partidasExclusivas.push(partida);
                    }
                } else {
                    // Verificar si la partida pertenece a alguna bolsa de proyecto para blindarla de ser absorbida
                    const perteneceAProyecto = bolsasProyecto.some(bp => {
                        const bpNombreNorm = (bp.nombre || '').toLowerCase().trim();
                        const bpId = String(bp.cuotaExtraAsociadaId || '');
                        return (bpId && partId === bpId) ||
                            (partDescNorm && (partDescNorm === bpNombreNorm || bpNombreNorm.includes(partDescNorm) || partDescNorm.includes(bpNombreNorm))) ||
                            (bpNombreNorm && partConceptoNorm.includes(bpNombreNorm));
                    });

                    if (!perteneceAProyecto) {
                        // Coincidencia con nombre de categoría si es pago de cuota extraordinaria con ese nombre
                        const coincideNombreCatConCuota = partDescNorm && (partDescNorm === catNombreNorm || catNombreNorm.includes(partDescNorm) || partDescNorm.includes(catNombreNorm));

                        if (coincideNombreCatConCuota || conceptosExclusivos.some(ce => partidaCoincideConConcepto(partida, ce))) {
                            partidasExclusivas.push(partida);
                            return;
                        }
                        const esCompartida = conceptosCompartidos.some(cc => partidaCoincideConConcepto(partida, cc));
                        if (esCompartida) {
                            partidasCompartidas.push(partida);
                        }
                    }
                }
            });

            const totalIngresosExclusivos = partidasExclusivas.reduce((sum, p) => sum + p.monto, 0);
            const totalFondoCompartido = partidasCompartidas.reduce((sum, p) => sum + p.monto, 0);
            const totalCapacidadBolsa = totalIngresosExclusivos + totalFondoCompartido;

            // Filtrar egresos de esta bolsa
            const egresosBolsa = egresosFiltrados.filter(eg => {
                const egCat = (eg.categoria || eg.categoriaEgreso || '').toLowerCase().trim();
                const egConc = (eg.concepto || eg.descripcion || '').toLowerCase().trim();

                if (egCat === catNombreNorm || egCat.includes(catNombreNorm) || catNombreNorm.includes(egCat)) {
                    return true;
                }
                if (conceptosEspecificos.length > 0 && conceptosEspecificos.some(ce => egConc.includes(ce) || egCat.includes(ce))) {
                    return true;
                }
                return false;
            });

            let totalEgresosBrutos = 0;
            let totalFijos = 0;
            let totalVariables = 0;
            let totalCompensadoRecibido = 0;
            let totalCompensadoDonado = 0;

            egresosBolsa.forEach(eg => {
                const m = Number(eg.montoNeto || eg.monto || 0);
                totalEgresosBrutos += m;
                const tipo = eg.tipoGasto || eg['Tipo de Gasto:'] || 'Variable';
                if (tipo === 'Fijo') {
                    totalFijos += m;
                } else {
                    totalVariables += m;
                }

                if (eg.compensacionBolsaCongelada?.activa) {
                    totalCompensadoRecibido += Number(eg.compensacionBolsaCongelada.montoCompensado || 0);
                }
            });

            // Verificar si esta bolsa donó fondos congelados a otras bolsas
            egresosFiltrados.forEach(eg => {
                if (eg.compensacionBolsaCongelada?.activa) {
                    const donanteId = eg.compensacionBolsaCongelada.bolsaDonanteId;
                    const donanteNombre = (eg.compensacionBolsaCongelada.bolsaDonanteNombre || eg.compensacionBolsaCongelada.bolsaDonante || '').toLowerCase().trim();
                    if ((donanteId && donanteId === cat.id) || (donanteNombre && donanteNombre === catNombreNorm)) {
                        totalCompensadoDonado += Number(eg.compensacionBolsaCongelada.montoCompensado || 0);
                    }
                }
            });

            // Gasto operativo efectivo (descontando la porción cubierta por fondos congelados)
            const totalEgresosOperativos = Math.max(0, totalEgresosBrutos - totalCompensadoRecibido);
            const excesoGastoSobreExclusivo = Math.max(0, totalEgresosOperativos - totalIngresosExclusivos);

            // Saldo congelado acumulado y disponible
            const saldoCongeladoBase = Number(cat.congelacion?.saldoCongeladoAcumulado || 0);
            const saldoCongeladoDisponible = Math.max(0, saldoCongeladoBase - totalCompensadoDonado);

            return {
                id: cat.id || `bolsa_${idx}`,
                nombre: cat.nombre,
                color: cat.color || COLORES_ZEN[idx % COLORES_ZEN.length],
                descripcion: cat.descripcion || '',
                esBolsaProyecto: Boolean(cat.esBolsaProyecto),
                cuotaExtraAsociadaId: cat.cuotaExtraAsociadaId || null,
                montoCuotaExtra: cat.montoCuotaExtra != null ? Number(cat.montoCuotaExtra) : null,
                frecuenciaCuotaExtra: cat.frecuenciaCuotaExtra || 'monthly',
                duracionMesesCuotaExtra: cat.duracionMesesCuotaExtra != null ? Number(cat.duracionMesesCuotaExtra) : null,
                congelacionHabilitada: Boolean(cat.congelacion?.habilitada),
                periodicidadCongelacion: cat.congelacion?.periodicidad || 'mensual',
                saldoCongelado: saldoCongeladoDisponible,
                totalCompensadoRecibido,
                totalCompensadoDonado,
                conceptosIngresoAsociados: conceptosIngreso,
                conceptosEspecificos: cat.conceptos || [],
                conceptosExclusivos,
                conceptosCompartidos,
                esBolsaMadreCompartida,
                categoriasVinculadas,
                totalIngresosExclusivos,
                totalFondoCompartido,
                totalCapacidadBolsa,
                totalIngresos: totalCapacidadBolsa,
                totalEgresos: totalEgresosBrutos,
                totalEgresosOperativos,
                totalFijos,
                totalVariables,
                excesoGastoSobreExclusivo,
                partidasExclusivas,
                partidasCompartidas,
                movimientosIngreso: [...partidasExclusivas, ...partidasCompartidas],
                movimientosEgreso: egresosBolsa,
            };
        });

        // Paso 2: Conciliación de Alternativa B (Fondo Común Desglosado con deducción en tiempo real)
        return analisisInicial.map(bolsa => {
            let totalGastadoPorHermanas = 0;

            if (bolsa.esBolsaMadreCompartida) {
                // Calcular cuánto gastaron las categorías hermanas del fondo común
                bolsa.categoriasVinculadas.forEach(nombreHermana => {
                    const hermana = analisisInicial.find(b => b.nombre === nombreHermana);
                    if (hermana) {
                        totalGastadoPorHermanas += hermana.excesoGastoSobreExclusivo;
                    }
                });
            }

            // Las hermanas solo pueden consumir hasta el total del fondo común (blindaje de fondos exclusivos)
            const consumoHermanasDeFondoComun = Math.min(totalGastadoPorHermanas, bolsa.totalFondoCompartido);

            // Remanente disponible en el fondo común
            const fondoCompartidoRemanente = Math.max(
                0,
                bolsa.totalFondoCompartido - consumoHermanasDeFondoComun - bolsa.excesoGastoSobreExclusivo
            );

            // Remanente en fondos exclusivos
            const fondoExclusivoRemanente = Math.max(
                0,
                bolsa.totalIngresosExclusivos - bolsa.totalEgresosOperativos
            );

            // Saldo operativo disponible total
            let saldoDisponible = 0;
            if (bolsa.excesoGastoSobreExclusivo > 0) {
                const disponibleEnComunParaBolsa = Math.max(0, bolsa.totalFondoCompartido - consumoHermanasDeFondoComun);
                saldoDisponible = disponibleEnComunParaBolsa - bolsa.excesoGastoSobreExclusivo;
            } else {
                saldoDisponible = fondoExclusivoRemanente + fondoCompartidoRemanente;
            }

            // Gasto efectivo atribuible
            const gastoEfectivoTotal = bolsa.totalEgresosOperativos + consumoHermanasDeFondoComun;
            const porcentajeEjercido = bolsa.totalCapacidadBolsa > 0
                ? (gastoEfectivoTotal / bolsa.totalCapacidadBolsa) * 100
                : (gastoEfectivoTotal > 0 ? 100 : 0);

            let estadoSalud = 'saludable';
            if (saldoDisponible < 0) {
                estadoSalud = 'deficit';
            } else if (porcentajeEjercido > 85) {
                estadoSalud = 'alerta';
            }

            const saldoTotal = saldoDisponible + bolsa.saldoCongelado;

            return {
                ...bolsa,
                totalGastadoPorHermanas: consumoHermanasDeFondoComun,
                fondoCompartidoRemanente,
                fondoExclusivoRemanente,
                saldoDisponible,
                saldoTotal,
                porcentajeEjercido: Math.min(porcentajeEjercido, 999),
                estadoSalud,
            };
        });
    }, [categoriasEgreso, partidasIngresosFiltradas, egresosFiltrados]);

    // Métricas Globales Consolidadas
    const resumenGlobal = useMemo(() => {
        const totalIngresos = ingresosFiltrados.reduce((sum, ing) => {
            const m = Number(ing.montoTotal != null ? ing.montoTotal : (ing.montoNeto != null ? ing.montoNeto : (ing.monto != null ? ing.monto : 0)));
            return sum + m;
        }, 0);
        let totalEgresos = 0;
        let totalFijos = 0;
        let totalVariables = 0;

        egresosFiltrados.forEach(eg => {
            const m = Number(eg.montoNeto || eg.monto || 0);
            totalEgresos += m;
            const tipo = eg.tipoGasto || eg['Tipo de Gasto:'] || 'Variable';
            if (tipo === 'Fijo') totalFijos += m;
            else totalVariables += m;
        });

        const saldoNeto = totalIngresos - totalEgresos;
        const ratioCoberturaFijos = totalFijos > 0 ? ((totalIngresos / totalFijos) * 100).toFixed(0) : '100+';

        // Identificar bolsa con mayor saldo y mayor gasto
        let bolsaMayorSaldo = null;
        let bolsaMayorPresion = null;

        metricasBolsas.forEach(b => {
            if (!bolsaMayorSaldo || b.saldoDisponible > bolsaMayorSaldo.saldoDisponible) {
                bolsaMayorSaldo = b;
            }
            if (!bolsaMayorPresion || b.totalEgresos > bolsaMayorPresion.totalEgresos) {
                bolsaMayorPresion = b;
            }
        });

        return {
            totalIngresos,
            totalEgresos,
            totalFijos,
            totalVariables,
            saldoNeto,
            ratioCoberturaFijos,
            bolsaMayorSaldo,
            bolsaMayorPresion,
        };
    }, [ingresosFiltrados, egresosFiltrados, metricasBolsas]);

    // Filtrar bolsas para visualización de tarjetas y gráficas si hay selector activo
    const bolsasParaMostrar = useMemo(() => {
        if (bolsaSeleccionada === 'todas') return metricasBolsas;
        return metricasBolsas.filter(b => b.nombre === bolsaSeleccionada);
    }, [metricasBolsas, bolsaSeleccionada]);

    // Datos formateados para Recharts
    const dataGraficaComparativa = useMemo(() => {
        return bolsasParaMostrar.map(b => ({
            name: b.nombre.length > 14 ? b.nombre.substring(0, 12) + '...' : b.nombre,
            nombreCompleto: b.nombre,
            esCompartida: b.esBolsaMadreCompartida,
            categoriasVinculadas: b.categoriasVinculadas,
            RecaudadoExclusivo: b.totalIngresosExclusivos,
            FondoComun: b.totalFondoCompartido,
            Recaudado: b.totalCapacidadBolsa,
            Gastado: b.totalEgresos,
            GastadoVinculadas: b.totalGastadoPorHermanas,
            Saldo: b.saldoDisponible,
        }));
    }, [bolsasParaMostrar]);

    const dataGraficaDistribucion = useMemo(() => {
        return bolsasParaMostrar
            .filter(b => b.totalEgresos > 0)
            .map((b, i) => ({
                name: b.nombre,
                value: b.totalEgresos,
                color: b.color || COLORES_ZEN[i % COLORES_ZEN.length],
                fijos: b.totalFijos,
                variables: b.totalVariables
            }));
    }, [bolsasParaMostrar]);

    // Exportar Informe a Excel Profesional (Alternativa B: Fondo Común Desglosado)
    const handleExportarExcel = () => {
        const wb = XLSX.utils.book_new();

        // Hoja 1: Resumen de Bolsas Financieras
        const resumenRows = [
            ['INFORME DE BOLSAS FINANCIERAS Y CONCILIACIÓN PRESUPUESTAL (FONDO COMÚN DESGLOSADO)'],
            [`Fraccionamiento: ${receiptConfig?.condominioNombre || userData?.fraccionamientoNombre || 'Residencial'}`],
            [`Período: ${fechaInicio || 'Histórico'} al ${fechaFin || 'Presente'}`],
            [''],
            [
                'Bolsa / Categoría',
                'Modalidad',
                'Recaudado Exclusivo',
                'Fondo Común Compartido',
                'Capacidad Total Bolsa',
                'Gastos Fijos',
                'Gastos Variables',
                'Total Gastado Propio',
                'Consumo por Vinculadas',
                'Saldo Operativo',
                'Fondo Congelado',
                'Saldo Total',
                '% Ejercido',
                'Estado de Salud'
            ]
        ];

        metricasBolsas.forEach(b => {
            resumenRows.push([
                b.nombre,
                b.esBolsaProyecto ? 'Bolsa Proyecto Especial' : (b.esBolsaMadreCompartida ? `Fondo Común (con: ${b.categoriasVinculadas.join(', ')})` : 'Bolsa Exclusiva'),
                b.totalIngresosExclusivos,
                b.totalFondoCompartido,
                b.totalCapacidadBolsa,
                b.totalFijos,
                b.totalVariables,
                b.totalEgresos,
                b.totalGastadoPorHermanas,
                b.saldoDisponible,
                b.saldoCongelado,
                b.saldoTotal,
                `${b.porcentajeEjercido.toFixed(1)}%`,
                b.estadoSalud === 'deficit' ? 'DÉFICIT' : (b.estadoSalud === 'alerta' ? 'ALERTA' : 'SALUDABLE')
            ]);
        });

        resumenRows.push(['']);
        resumenRows.push([
            'TOTAL CONSOLIDADO',
            'Consolidado Bancario',
            '-',
            '-',
            resumenGlobal.totalIngresos,
            resumenGlobal.totalFijos,
            resumenGlobal.totalVariables,
            resumenGlobal.totalEgresos,
            '-',
            resumenGlobal.saldoNeto,
            metricasBolsas.reduce((sum, b) => sum + (b.saldoCongelado || 0), 0),
            resumenGlobal.saldoNeto + metricasBolsas.reduce((sum, b) => sum + (b.saldoCongelado || 0), 0),
            `${resumenGlobal.totalIngresos > 0 ? ((resumenGlobal.totalEgresos / resumenGlobal.totalIngresos) * 100).toFixed(1) : 0}%`,
            resumenGlobal.saldoNeto >= 0 ? 'SUPERÁVIT' : 'DÉFICIT'
        ]);

        const wsResumen = XLSX.utils.aoa_to_sheet(resumenRows);
        wsResumen['!cols'] = [
            { wch: 28 }, { wch: 26 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 16 }
        ];
        XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen de Bolsas');

        // Hoja 2: Detalle de Egresos
        const egresosRows = [
            ['Fecha', 'Folio', 'Bolsa / Categoría', 'Concepto / Descripción', 'Tipo de Gasto', 'Proveedor', 'Referencia', 'Compensación Congelada', 'Monto Neto']
        ];
        egresosFiltrados.forEach(eg => {
            const f = parseFechaSegura(eg);
            const compLabel = eg.compensacionBolsaCongelada?.activa
                ? `Compensado: $${Number(eg.compensacionBolsaCongelada.montoCompensado || 0).toFixed(2)} (desde ${eg.compensacionBolsaCongelada.bolsaDonanteNombre || 'Congelado'})`
                : 'Sin compensación';

            egresosRows.push([
                f ? f.toLocaleDateString('es-MX') : 'N/A',
                eg.folio || 'N/A',
                eg.categoria || 'N/A',
                eg.descripcion || eg.concepto || 'N/A',
                eg.tipoGasto || eg['Tipo de Gasto:'] || 'Variable',
                eg.proveedor || 'N/A',
                eg.referenciaFactura || eg.referencia || 'N/A',
                compLabel,
                Number(eg.montoNeto || eg.monto || 0)
            ]);
        });
        const wsEgresos = XLSX.utils.aoa_to_sheet(egresosRows);
        wsEgresos['!cols'] = [
            { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 32 }, { wch: 14 }, { wch: 24 }, { wch: 16 }, { wch: 30 }, { wch: 16 }
        ];
        XLSX.utils.book_append_sheet(wb, wsEgresos, 'Detalle de Egresos');

        // Hoja 3: Detalle de Ingresos (Partidas Desglosadas)
        const ingresosRows = [
            ['Fecha', 'Folio Ticket', 'Concepto Partida', 'Tipo de Ingreso', 'Bolsa Asignada', 'Proyecto Asignado', 'Casa / Cliente', 'Método de Pago', 'Monto Partida']
        ];
        partidasIngresosFiltradas.forEach(p => {
            const f = p.fechaSegura || parseFechaSegura(p);
            const bolsaNombre = obtenerBolsaDeIngreso(p.concepto, categoriasEgreso, p);
            ingresosRows.push([
                f ? f.toLocaleDateString('es-MX') : 'N/A',
                p.folio || 'N/A',
                p.concepto || 'N/A',
                p.esMultiConcepto ? `Partida (${p.numeroPartida}/${p.totalPartidas})` : 'Ingreso Único',
                bolsaNombre,
                p.cuotaExtraDesc || '-',
                p.nombreCliente || 'S/N',
                p.metodoPago || 'N/A',
                Number(p.monto || 0)
            ]);
        });
        const wsIngresos = XLSX.utils.aoa_to_sheet(ingresosRows);
        wsIngresos['!cols'] = [
            { wch: 12 }, { wch: 16 }, { wch: 28 }, { wch: 18 }, { wch: 26 }, { wch: 24 }, { wch: 18 }, { wch: 16 }
        ];
        XLSX.utils.book_append_sheet(wb, wsIngresos, 'Detalle de Ingresos');

        const fechaLabel = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Bolsas_Financieras_${fechaLabel}.xlsx`);
    };

    // Imprimir o Guardar PDF
    const handleImprimir = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="metricas-bolsas-page">
                <div style={{ textAlign: 'center', padding: '80px 20px', color: '#64748b' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.95rem' }}>Cargando métricas de bolsas financieras...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="metricas-bolsas-page">
            {/* ENCABEZADO ZEN */}
            <div className="metricas-bolsas-header no-print">
                <div className="header-titles">
                    <div className="header-badge">
                        <FaShieldAlt style={{ marginRight: '6px' }} /> Control Presupuestal Inteligente
                    </div>
                    <h1>Métricas de Bolsas Financieras</h1>
                    <p>Conciliación en tiempo real: ingresos recaudados vs gastos operativos clasificados por bolsas canónicas.</p>
                </div>
                <div className="header-actions-group">
                    <Link
                        to="/configuraciones/recibos"
                        className="btn-zen-action outline"
                        title="Ir a Configuración de Recibos para editar categorías de egresos"
                    >
                        <FaCogs style={{ marginRight: '6px' }} /> Configurar Bolsas
                    </Link>
                    <button
                        type="button"
                        className="btn-zen-action outline"
                        onClick={handleImprimir}
                    >
                        <FaPrint style={{ marginRight: '6px' }} /> Imprimir / PDF
                    </button>
                    <button
                        type="button"
                        className="btn-zen-action primary"
                        onClick={handleExportarExcel}
                    >
                        <FaFileExcel style={{ marginRight: '6px' }} /> Exportar a Excel
                    </button>
                </div>
            </div>

            {/* BARRA DE FILTROS Y CONTROL TEMPORAL */}
            <div className="bolsas-filters-bar no-print">
                <div className="filter-period-buttons">
                    <button
                        type="button"
                        className={`btn-filter-pill ${periodo === 'semana' ? 'active' : ''}`}
                        onClick={() => handleCambiarPeriodo('semana')}
                    >
                        Semana
                    </button>
                    <button
                        type="button"
                        className={`btn-filter-pill ${periodo === 'mes' ? 'active' : ''}`}
                        onClick={() => handleCambiarPeriodo('mes')}
                    >
                        Mes
                    </button>
                    <button
                        type="button"
                        className={`btn-filter-pill ${periodo === 'año' ? 'active' : ''}`}
                        onClick={() => handleCambiarPeriodo('año')}
                    >
                        Año
                    </button>
                    <button
                        type="button"
                        className={`btn-filter-pill ${periodo === 'historico' ? 'active' : ''}`}
                        onClick={() => handleCambiarPeriodo('historico')}
                    >
                        Histórico
                    </button>
                    <button
                        type="button"
                        className={`btn-filter-pill ${periodo === 'custom' ? 'active' : ''}`}
                        onClick={() => setPeriodo('custom')}
                    >
                        Personalizado
                    </button>
                </div>

                {periodo === 'custom' && (
                    <div className="filter-dates-inline">
                        <label>Desde:</label>
                        <input
                            type="date"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                        <label>Hasta:</label>
                        <input
                            type="date"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                        />
                    </div>
                )}

                <div className="filter-selects-inline">
                    <div className="filter-input-wrap">
                        <FaLayerGroup className="filter-icon" />
                        <select
                            value={bolsaSeleccionada}
                            onChange={(e) => setBolsaSeleccionada(e.target.value)}
                            title="Filtrar por bolsa específica"
                        >
                            <option value="todas">Todas las Bolsas</option>
                            {metricasBolsas.map(b => (
                                <option key={b.id} value={b.nombre}>{b.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-input-wrap">
                        <select
                            value={filtroTipoGasto}
                            onChange={(e) => setFiltroTipoGasto(e.target.value)}
                            title="Filtrar tipo de gasto"
                        >
                            <option value="todos">Todos (Fijos + Variables)</option>
                            <option value="Fijo">Solo Costos Fijos</option>
                            <option value="Variable">Solo Gastos Variables</option>
                        </select>
                    </div>

                    <div className="filter-input-wrap search-box">
                        <FaSearch className="filter-icon" />
                        <input
                            type="text"
                            placeholder="Buscar concepto o proveedor..."
                            value={busquedaTexto}
                            onChange={(e) => setBusquedaTexto(e.target.value)}
                        />
                        {busquedaTexto && (
                            <button
                                type="button"
                                className="clear-search-btn"
                                onClick={() => setBusquedaTexto('')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* TARJETAS DE KPIS SUPERIORES (ZEN) */}
            <div className="bolsas-kpi-grid">
                <div className="bolsa-kpi-card ingresos">
                    <div className="kpi-icon-wrap green">
                        <FaArrowUp />
                    </div>
                    <div className="kpi-info">
                        <span className="kpi-label">Recaudación Total Fondeada</span>
                        <h2 className="kpi-value text-green">{formatCurrency(resumenGlobal.totalIngresos)}</h2>
                        <span className="kpi-subtext">
                            {ingresosFiltrados.length} registros en el período
                        </span>
                    </div>
                </div>

                <div className="bolsa-kpi-card egresos">
                    <div className="kpi-icon-wrap red">
                        <FaArrowDown />
                    </div>
                    <div className="kpi-info">
                        <span className="kpi-label">Egresos Totales del Período</span>
                        <h2 className="kpi-value text-red">{formatCurrency(resumenGlobal.totalEgresos)}</h2>
                        <div className="kpi-micro-tags">
                            <span className="micro-tag blue">Fijos: {formatCurrency(resumenGlobal.totalFijos)}</span>
                            <span className="micro-tag amber">Variables: {formatCurrency(resumenGlobal.totalVariables)}</span>
                        </div>
                    </div>
                </div>

                <div className={`bolsa-kpi-card balance ${resumenGlobal.saldoNeto >= 0 ? 'positivo' : 'negativo'}`}>
                    <div className={`kpi-icon-wrap ${resumenGlobal.saldoNeto >= 0 ? 'emerald' : 'rose'}`}>
                        <FaWallet />
                    </div>
                    <div className="kpi-info">
                        <span className="kpi-label">Balance Neto Global</span>
                        <h2 className={`kpi-value ${resumenGlobal.saldoNeto >= 0 ? 'text-emerald' : 'text-rose'}`}>
                            {formatCurrency(resumenGlobal.saldoNeto)}
                        </h2>
                        <span className="kpi-status-badge">
                            {resumenGlobal.saldoNeto >= 0 ? '✓ Superávit Presupuestal' : '⚠ Déficit en el Período'}
                        </span>
                    </div>
                </div>

                <div className="bolsa-kpi-card runway">
                    <div className="kpi-icon-wrap indigo">
                        <FaShieldAlt />
                    </div>
                    <div className="kpi-info">
                        <span className="kpi-label">Cobertura de Costos Fijos</span>
                        <h2 className="kpi-value text-indigo">{resumenGlobal.ratioCoberturaFijos}%</h2>
                        <span className="kpi-subtext">
                            Ratio de solvencia sobre contratos y servicios esenciales
                        </span>
                    </div>
                </div>
            </div>

            {/* GRID DE BOLSAS FINANCIERAS (TARJETAS INDIVIDUALES) */}
            <div className="bolsas-cards-section">
                <div className="section-title-row">
                    <h3>Estado y Salud de Cada Bolsa</h3>
                    <span className="section-subtitle">
                        {bolsasParaMostrar.length} bolsa(s) activa(s) • Conciliación fondo por fondo
                    </span>
                </div>

                <div className="bolsas-cards-container">
                    {bolsasParaMostrar.map(bolsa => {
                        const pct = Math.min(Math.round(bolsa.porcentajeEjercido), 100);
                        const esDeficit = bolsa.saldoDisponible < 0;

                        return (
                            <div
                                key={bolsa.id}
                                className="bolsa-metric-card"
                                style={{ borderTop: `4px solid ${bolsa.color}` }}
                            >
                                <div className="card-top-header">
                                    <div className="card-title-col">
                                        <div className="card-badge-color" style={{ backgroundColor: bolsa.color }} />
                                        <h4>{bolsa.nombre}</h4>
                                        {bolsa.esBolsaProyecto && (
                                            <span className="bolsa-badge-proyecto" title="Bolsa de ahorro para proyecto especial con fondeo exclusivo">
                                                🚀 Proyecto
                                            </span>
                                        )}
                                        {bolsa.congelacionHabilitada && (
                                            <span className="bolsa-badge-congelada" title={`Fondo congelado con periodicidad: ${bolsa.periodicidadCongelacion}`}>
                                                ❄️ {bolsa.periodicidadCongelacion}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`health-pill ${bolsa.estadoSalud}`}>
                                        {bolsa.estadoSalud === 'deficit' && 'Déficit'}
                                        {bolsa.estadoSalud === 'alerta' && 'Alerta 85%+'}
                                        {bolsa.estadoSalud === 'saludable' && 'Saludable'}
                                    </span>
                                </div>

                                {bolsa.descripcion && (
                                    <p className="card-desc-text">{bolsa.descripcion}</p>
                                )}

                                {bolsa.esBolsaMadreCompartida && (
                                    <div className="card-shared-pool-pill" title={`Comparte conceptos (${bolsa.conceptosCompartidos?.join(', ')}) con: ${bolsa.categoriasVinculadas.join(', ')}`}>
                                        <FaExchangeAlt size={10} style={{ marginRight: '5px' }} />
                                        <span>Fondo Común con: <strong>{bolsa.categoriasVinculadas.join(', ')}</strong></span>
                                    </div>
                                )}

                                {/* Barra de Progreso de Presupuesto */}
                                <div className="progress-bar-wrap">
                                    <div className="progress-labels">
                                        <span>Ejercido: {bolsa.porcentajeEjercido.toFixed(1)}%</span>
                                        <span className={esDeficit ? 'text-red' : 'text-green'}>
                                            Remanente Operativo: {formatCurrency(bolsa.saldoDisponible)}
                                        </span>
                                    </div>
                                    <div className="progress-track">
                                        <div
                                            className={`progress-fill ${esDeficit ? 'danger' : (pct > 85 ? 'warning' : 'ok')}`}
                                            style={{ width: `${pct}%`, backgroundColor: esDeficit ? '#ef4444' : bolsa.color }}
                                        />
                                    </div>
                                </div>

                                {/* Desglose de Fondo Congelado Reservado */}
                                {bolsa.saldoCongelado > 0 && (
                                    <div className="card-congelado-pill">
                                        <div className="congelado-row">
                                            <span>❄️ Fondo Congelado Reservado:</span>
                                            <strong className="text-cyan">{formatCurrency(bolsa.saldoCongelado)}</strong>
                                        </div>
                                        <div className="congelado-total-row">
                                            <span>Saldo Total (Operativo + Congelado):</span>
                                            <strong className="text-emerald">{formatCurrency(bolsa.saldoTotal)}</strong>
                                        </div>
                                    </div>
                                )}

                                {/* Avisos de compensación si aplican */}
                                {bolsa.totalCompensadoRecibido > 0 && (
                                    <div className="compensacion-tag-info recibido">
                                        <span>🛡️ Déficit cubierto con {formatCurrency(bolsa.totalCompensadoRecibido)} de fondos congelados</span>
                                    </div>
                                )}
                                {bolsa.totalCompensadoDonado > 0 && (
                                    <div className="compensacion-tag-info donado">
                                        <span>❄️ Aportó {formatCurrency(bolsa.totalCompensadoDonado)} para compensar otras bolsas</span>
                                    </div>
                                )}

                                {/* Valores numéricos de la bolsa (Alternativa B: Fondo Común Desglosado) */}
                                <div className="card-stats-grid">
                                    {bolsa.esBolsaMadreCompartida ? (
                                        <>
                                            {bolsa.totalIngresosExclusivos > 0 && (
                                                <div className="stat-item">
                                                    <span className="stat-label">Recaudado Exclusivo:</span>
                                                    <span className="stat-value text-green">{formatCurrency(bolsa.totalIngresosExclusivos)}</span>
                                                </div>
                                            )}
                                            <div className="stat-item">
                                                <span className="stat-label">Fondo Común Compartido:</span>
                                                <span className="stat-value text-blue">{formatCurrency(bolsa.totalFondoCompartido)}</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Capacidad Bolsa:</span>
                                                <span className="stat-value font-semibold">{formatCurrency(bolsa.totalCapacidadBolsa)}</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Gastado Propio:</span>
                                                <span className="stat-value text-red">{formatCurrency(bolsa.totalEgresos)}</span>
                                            </div>
                                            {bolsa.totalGastadoPorHermanas > 0 && (
                                                <div className="stat-item full-width-stat">
                                                    <span className="stat-label">Consumo común por vinculadas:</span>
                                                    <span className="stat-value text-amber-dark">-{formatCurrency(bolsa.totalGastadoPorHermanas)}</span>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="stat-item">
                                                <span className="stat-label">Recaudado Bolsa:</span>
                                                <span className="stat-value text-green">{formatCurrency(bolsa.totalCapacidadBolsa)}</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Gastado Propio:</span>
                                                <span className="stat-value text-red">{formatCurrency(bolsa.totalEgresos)}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="card-mini-breakdown">
                                    <span className="breakdown-pill">
                                        Fijo: <strong>{formatCurrency(bolsa.totalFijos)}</strong>
                                    </span>
                                    <span className="breakdown-pill">
                                        Variable: <strong>{formatCurrency(bolsa.totalVariables)}</strong>
                                    </span>
                                </div>

                                {bolsa.esBolsaProyecto && (
                                    <Link
                                        to="/cuotas-extraordinarias"
                                        className="btn-link-cuotas-proyecto"
                                        title="Ver recaudación y estado de la cuota extraordinaria"
                                    >
                                        🎯 Ver Medidor de Avance del Proyecto →
                                    </Link>
                                )}

                                <button
                                    type="button"
                                    className="btn-ver-movimientos-bolsa"
                                    onClick={() => handleAbrirDetalleModal(bolsa)}
                                >
                                    Ver {bolsa.movimientosEgreso.length + bolsa.movimientosIngreso.length} Movimientos →
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* SECCIÓN DE GRÁFICAS INTERACTIVAS */}
            <div className="bolsas-charts-container">
                <div className="charts-header-tabs no-print">
                    <div className="charts-tab-buttons">
                        <button
                            type="button"
                            className={`chart-tab-btn ${tabGrafica === 'comparativa' ? 'active' : ''}`}
                            onClick={() => setTabGrafica('comparativa')}
                        >
                            📊 Recaudación vs Gastos por Bolsa
                        </button>
                        <button
                            type="button"
                            className={`chart-tab-btn ${tabGrafica === 'distribucion' ? 'active' : ''}`}
                            onClick={() => setTabGrafica('distribucion')}
                        >
                            🍩 Distribución de Gastos (Donut)
                        </button>
                    </div>
                </div>

                <div className="chart-canvas-box">
                    {tabGrafica === 'comparativa' && (
                        <div>
                            <h4 className="chart-title">Comparativa: Capacidad Recaudada vs Gastos por Bolsa</h4>
                            <p className="chart-subtitle">Permite verificar visualmente qué fondos operan con superávit y cuáles requieren atención presupuestal.</p>
                            <div style={{ width: '100%', height: 350 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={dataGraficaComparativa}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} />
                                        <YAxis
                                            stroke="#64748b"
                                            fontSize={12}
                                            tickLine={false}
                                            tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                                        />
                                        <Tooltip content={<CustomBarTooltip />} />
                                        <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                        <Bar name="Capacidad Recaudada" dataKey="Recaudado" fill="#10b981" radius={[6, 6, 0, 0]} />
                                        <Bar name="Gastado Propio" dataKey="Gastado" fill="#ef4444" radius={[6, 6, 0, 0]} />
                                        <Bar name="Saldo Remanente" dataKey="Saldo" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="chart-zen-note">
                                <FaInfoCircle style={{ color: '#3b82f6', flexShrink: 0 }} />
                                <span>
                                    <strong>Alternativa B (Fondo Común Desglosado):</strong> Las bolsas vinculadas que comparten conceptos de ingreso reflejan su capacidad presupuestal y descuentan en tiempo real los egresos ejecutados por las categorías hermanas, garantizando que los fondos comunes estén disponibles íntegramente donde más se requiera sin cuotas prorrateadas rígidas.
                                </span>
                            </div>
                        </div>
                    )}

                    {tabGrafica === 'distribucion' && (
                        <div>
                            <h4 className="chart-title">Distribución de Gastos por Categoría</h4>
                            <p className="chart-subtitle">Desglose porcentual del gasto total del período.</p>
                            {dataGraficaDistribucion.length === 0 ? (
                                <div className="no-data-chart">No hay egresos registrados en el período seleccionado.</div>
                            ) : (
                                <div style={{ width: '100%', height: 350 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={dataGraficaDistribucion}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={70}
                                                outerRadius={115}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            >
                                                {dataGraficaDistribucion.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatCurrency(value)} />
                                            <Legend wrapperStyle={{ paddingTop: '15px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* TABLA DE AUDITORÍA Y MOVIMIENTOS RECIENTES */}
            <div className="bolsas-audit-table-section">
                <div className="section-title-row audit-header-row">
                    <div>
                        <h3>Movimientos Detallados del Período</h3>
                        <span className="section-subtitle">
                            {tabAuditoria === 'egresos'
                                ? `Auditoría de egresos clasificados (${egresosFiltrados.length} registros)`
                                : `Auditoría de partidas de ingresos fondeadas (${partidasIngresosFiltradas.length} partidas)`}
                        </span>
                    </div>
                    <div className="audit-subtabs">
                        <button
                            type="button"
                            className={`btn-subtab ${tabAuditoria === 'egresos' ? 'active' : ''}`}
                            onClick={() => setTabAuditoria('egresos')}
                        >
                            Egresos ({egresosFiltrados.length})
                        </button>
                        <button
                            type="button"
                            className={`btn-subtab ${tabAuditoria === 'ingresos' ? 'active' : ''}`}
                            onClick={() => setTabAuditoria('ingresos')}
                        >
                            Ingresos Fondeados ({partidasIngresosFiltradas.length})
                        </button>
                    </div>
                </div>

                {tabAuditoria === 'egresos' ? (
                    <div className="audit-table-wrap">
                        <table className="audit-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Folio</th>
                                    <th>Bolsa / Categoría</th>
                                    <th>Concepto / Proveedor</th>
                                    <th>Tipo de Gasto</th>
                                    <th>Método</th>
                                    <th>Monto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {egresosFiltrados.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                                            No se encontraron registros de egresos para los filtros seleccionados.
                                        </td>
                                    </tr>
                                ) : (
                                    egresosFiltrados.slice(0, 30).map(eg => {
                                        const f = parseFechaSegura(eg);
                                        const tipo = eg.tipoGasto || eg['Tipo de Gasto:'] || 'Variable';

                                        return (
                                            <tr key={eg.id}>
                                                <td>{f ? f.toLocaleDateString('es-MX') : 'N/A'}</td>
                                                <td><strong>{eg.folio || 'N/A'}</strong></td>
                                                <td>
                                                    <span className="categoria-cell-tag">{eg.categoria || 'General'}</span>
                                                    {eg.compensacionBolsaCongelada?.activa && (
                                                        <span className="compensacion-mini-pill" title={`Compensado con ${formatCurrency(eg.compensacionBolsaCongelada.montoCompensado)} de ${eg.compensacionBolsaCongelada.bolsaDonanteNombre || 'Fondo Congelado'}`}>
                                                            ❄️ Compensado
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="concepto-prov-cell">
                                                        <strong>{eg.descripcion || eg.concepto || 'Egreso'}</strong>
                                                        {eg.proveedor && <small>Prov: {eg.proveedor}</small>}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`tipo-pill ${tipo === 'Fijo' ? 'fijo' : 'variable'}`}>
                                                        {tipo}
                                                    </span>
                                                </td>
                                                <td>{eg.metodoPago || 'N/A'}</td>
                                                <td className="monto-cell text-red">
                                                    -{formatCurrency(eg.montoNeto || eg.monto)}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="audit-table-wrap">
                        <table className="audit-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Folio Ticket</th>
                                    <th>Bolsa Asignada</th>
                                    <th>Concepto de Ingreso</th>
                                    <th>Cliente / Casa</th>
                                    <th>Método</th>
                                    <th>Monto Partida</th>
                                </tr>
                            </thead>
                            <tbody>
                                {partidasIngresosFiltradas.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                                            No se encontraron registros de ingresos para los filtros seleccionados.
                                        </td>
                                    </tr>
                                ) : (
                                    partidasIngresosFiltradas.slice(0, 30).map(partida => {
                                        const f = partida.fechaSegura || parseFechaSegura(partida);
                                        const bolsaNombre = obtenerBolsaDeIngreso(partida.concepto, categoriasEgreso, partida);

                                        return (
                                            <tr key={partida.id}>
                                                <td>{f ? f.toLocaleDateString('es-MX') : 'N/A'}</td>
                                                <td>
                                                    <strong>{partida.folio || 'N/A'}</strong>
                                                    {partida.esMultiConcepto && (
                                                        <span className="partida-ticket-badge" title={`Partida ${partida.numeroPartida} de ${partida.totalPartidas}`}>
                                                            {partida.numeroPartida}/{partida.totalPartidas}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="categoria-cell-tag">{bolsaNombre}</span>
                                                </td>
                                                <td>
                                                    <div className="concepto-prov-cell">
                                                        <strong>{partida.concepto}</strong>
                                                        {partida.cuotaExtraDesc && (
                                                            <small className="proyecto-partida-sub">🚀 {partida.cuotaExtraDesc}</small>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{partida.nombreCliente || 'S/N'}</td>
                                                <td>{partida.metodoPago || 'N/A'}</td>
                                                <td className="monto-cell text-green">
                                                    +{formatCurrency(partida.monto)}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* MODAL DETALLE DE BOLSA ESPECÍFICA */}
            {bolsaDetalleModal && (
                <div className="bolsa-modal-overlay" onClick={() => setBolsaDetalleModal(null)}>
                    <div className="bolsa-modal-card wide" onClick={e => e.stopPropagation()}>
                        <div className="bolsa-modal-header">
                            <div>
                                <h3>Detalle de Bolsa: {bolsaDetalleModal.nombre}</h3>
                                <div className="modal-header-metrics-row">
                                    <span className="modal-metric-chip green">
                                        Saldo Disponible: <strong>{formatCurrency(bolsaDetalleModal.saldoDisponible)}</strong>
                                    </span>
                                    <span className="modal-metric-chip purple">
                                        Capacidad Total: <strong>{formatCurrency(bolsaDetalleModal.totalCapacidadBolsa)}</strong>
                                    </span>
                                    <span className="modal-metric-chip orange">
                                        Gastado Propio: <strong>{formatCurrency(bolsaDetalleModal.totalEgresos)}</strong>
                                    </span>
                                    {bolsaDetalleModal.totalGastadoPorHermanas > 0 && (
                                        <span className="modal-metric-chip amber">
                                            Consumo por Vinculadas: <strong>-{formatCurrency(bolsaDetalleModal.totalGastadoPorHermanas)}</strong>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="bolsa-modal-close"
                                onClick={() => setBolsaDetalleModal(null)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="bolsa-modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            {/* Pestañas Internas Zen para No Apilar Tablas */}
                            <div className="modal-subtabs-bar">
                                <button
                                    type="button"
                                    className={`btn-modal-subtab ${tabModalMovimientos === 'ingresos' ? 'active ingresos' : ''}`}
                                    onClick={() => setTabModalMovimientos('ingresos')}
                                >
                                    📥 Ingresos Fondeados ({bolsaDetalleModal.movimientosIngreso.length})
                                </button>
                                <button
                                    type="button"
                                    className={`btn-modal-subtab ${tabModalMovimientos === 'egresos' ? 'active egresos' : ''}`}
                                    onClick={() => setTabModalMovimientos('egresos')}
                                >
                                    📤 Egresos Cargados ({bolsaDetalleModal.movimientosEgreso.length})
                                </button>
                            </div>

                            {/* TAB: INGRESOS FONDEADOS */}
                            {tabModalMovimientos === 'ingresos' ? (
                                <div>
                                    {bolsaDetalleModal.movimientosIngreso.length === 0 ? (
                                        <p style={{ color: '#94a3b8', fontStyle: 'italic', padding: '24px 0', textAlign: 'center' }}>
                                            Sin ingresos fondeados en este período.
                                        </p>
                                    ) : (
                                        <>
                                            <table className="audit-table modal-table">
                                                <thead>
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Concepto de Ingreso</th>
                                                        <th>Tipo de Fondo</th>
                                                        <th>Método</th>
                                                        <th>Monto</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const totalIngresos = bolsaDetalleModal.movimientosIngreso.length;
                                                        const totalPaginas = Math.ceil(totalIngresos / REGISTROS_POR_PAGINA_MODAL) || 1;
                                                        const pagActual = Math.min(paginaModalIngresos, totalPaginas);
                                                        const inicio = (pagActual - 1) * REGISTROS_POR_PAGINA_MODAL;
                                                        const partidasPagina = bolsaDetalleModal.movimientosIngreso.slice(inicio, inicio + REGISTROS_POR_PAGINA_MODAL);

                                                        return partidasPagina.map(ing => {
                                                            const f = ing.fechaSegura || parseFechaSegura(ing);
                                                            const esCompartido = bolsaDetalleModal.conceptosCompartidos?.some(cc => {
                                                                const cNorm = (cc || '').toLowerCase().trim();
                                                                const iNorm = (ing.concepto || '').toLowerCase().trim();
                                                                return cNorm === iNorm || cNorm.includes(iNorm) || iNorm.includes(cNorm);
                                                            });

                                                            const infoCasa = ing.rawIngreso?.casaNumero 
                                                                ? `Casa ${ing.rawIngreso.casaNumero}` 
                                                                : (ing.nombreCliente && ing.nombreCliente !== 'S/N' ? ing.nombreCliente : '');
                                                            const tooltipTicket = `Partida del Ticket Folio: ${ing.folio}${infoCasa ? ` • ${infoCasa}` : ''}`;

                                                            return (
                                                                <tr key={ing.id}>
                                                                    <td>{f ? f.toLocaleDateString('es-MX') : 'N/A'}</td>
                                                                    <td>
                                                                        <strong>{ing.concepto || 'N/A'}</strong>
                                                                        {ing.esMultiConcepto && (
                                                                            <span className="partida-ticket-badge" title={tooltipTicket}>
                                                                                Ticket {ing.folio}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {esCompartido ? (
                                                                            <span className="badge-fondo-comun" title={`Compartido con: ${bolsaDetalleModal.categoriasVinculadas.join(', ')}`}>
                                                                                🔗 Fondo Común
                                                                            </span>
                                                                        ) : (
                                                                            <span className="badge-fondo-exclusivo">
                                                                                Exclusivo
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td>{ing.metodoPago || 'N/A'}</td>
                                                                    <td className="text-green">+{formatCurrency(ing.monto)}</td>
                                                                </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>

                                            {/* Paginación Condicional Zen para Ingresos */}
                                            {bolsaDetalleModal.movimientosIngreso.length > REGISTROS_POR_PAGINA_MODAL && (() => {
                                                const totalIngresos = bolsaDetalleModal.movimientosIngreso.length;
                                                const totalPaginas = Math.ceil(totalIngresos / REGISTROS_POR_PAGINA_MODAL);
                                                const inicio = (paginaModalIngresos - 1) * REGISTROS_POR_PAGINA_MODAL;

                                                return (
                                                    <div className="modal-pagination-bar">
                                                        <span className="pagination-info">
                                                            Mostrando {inicio + 1} - {Math.min(inicio + REGISTROS_POR_PAGINA_MODAL, totalIngresos)} de {totalIngresos} partidas
                                                        </span>
                                                        <div className="pagination-controls">
                                                            <button
                                                                type="button"
                                                                className="btn-page-nav"
                                                                disabled={paginaModalIngresos <= 1}
                                                                onClick={() => setPaginaModalIngresos(p => Math.max(1, p - 1))}
                                                            >
                                                                ◀ Anterior
                                                            </button>
                                                            <span className="page-current">
                                                                Página {paginaModalIngresos} de {totalPaginas}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn-page-nav"
                                                                disabled={paginaModalIngresos >= totalPaginas}
                                                                onClick={() => setPaginaModalIngresos(p => Math.min(totalPaginas, p + 1))}
                                                            >
                                                                Siguiente ▶
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>
                            ) : (
                                /* TAB: EGRESOS CARGADOS */
                                <div>
                                    {bolsaDetalleModal.movimientosEgreso.length === 0 ? (
                                        <p style={{ color: '#94a3b8', fontStyle: 'italic', padding: '24px 0', textAlign: 'center' }}>
                                            Sin egresos cargados en este período.
                                        </p>
                                    ) : (
                                        <>
                                            <table className="audit-table modal-table">
                                                <thead>
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Concepto</th>
                                                        <th>Tipo</th>
                                                        <th>Proveedor</th>
                                                        <th>Monto</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const totalEgresos = bolsaDetalleModal.movimientosEgreso.length;
                                                        const totalPaginas = Math.ceil(totalEgresos / REGISTROS_POR_PAGINA_MODAL) || 1;
                                                        const pagActual = Math.min(paginaModalEgresos, totalPaginas);
                                                        const inicio = (pagActual - 1) * REGISTROS_POR_PAGINA_MODAL;
                                                        const egresosPagina = bolsaDetalleModal.movimientosEgreso.slice(inicio, inicio + REGISTROS_POR_PAGINA_MODAL);

                                                        return egresosPagina.map(eg => {
                                                            const f = parseFechaSegura(eg);
                                                            const tipo = eg.tipoGasto || eg['Tipo de Gasto:'] || 'Variable';
                                                            return (
                                                                <tr key={eg.id}>
                                                                    <td>{f ? f.toLocaleDateString('es-MX') : 'N/A'}</td>
                                                                    <td>{eg.descripcion || eg.concepto || 'N/A'}</td>
                                                                    <td><span className={`tipo-pill ${tipo === 'Fijo' ? 'fijo' : 'variable'}`}>{tipo}</span></td>
                                                                    <td>{eg.proveedor || 'N/A'}</td>
                                                                    <td className="text-red">-{formatCurrency(eg.montoNeto || eg.monto)}</td>
                                                                </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>

                                            {/* Paginación Condicional Zen para Egresos */}
                                            {bolsaDetalleModal.movimientosEgreso.length > REGISTROS_POR_PAGINA_MODAL && (() => {
                                                const totalEgresos = bolsaDetalleModal.movimientosEgreso.length;
                                                const totalPaginas = Math.ceil(totalEgresos / REGISTROS_POR_PAGINA_MODAL);
                                                const inicio = (paginaModalEgresos - 1) * REGISTROS_POR_PAGINA_MODAL;

                                                return (
                                                    <div className="modal-pagination-bar">
                                                        <span className="pagination-info">
                                                            Mostrando {inicio + 1} - {Math.min(inicio + REGISTROS_POR_PAGINA_MODAL, totalEgresos)} de {totalEgresos} egresos
                                                        </span>
                                                        <div className="pagination-controls">
                                                            <button
                                                                type="button"
                                                                className="btn-page-nav"
                                                                disabled={paginaModalEgresos <= 1}
                                                                onClick={() => setPaginaModalEgresos(p => Math.max(1, p - 1))}
                                                            >
                                                                ◀ Anterior
                                                            </button>
                                                            <span className="page-current">
                                                                Página {paginaModalEgresos} de {totalPaginas}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn-page-nav"
                                                                disabled={paginaModalEgresos >= totalPaginas}
                                                                onClick={() => setPaginaModalEgresos(p => Math.min(totalPaginas, p + 1))}
                                                            >
                                                                Siguiente ▶
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="bolsa-modal-footer">
                            <button
                                type="button"
                                className="btn-zen-primary"
                                onClick={() => setBolsaDetalleModal(null)}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MetricasBolsasFinancieras;
